import Modal from 'tailwind-components/Modal/Modal';
import ModalActionConfirmation from 'components/ModalActionConfirmation/ModalActionConfirmation';
import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import { TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { handleSaveFilter, handleUpdateFilter } from 'state/actions/filters';
import { showAlert } from 'state/actions/site';

export default function SaveFilterModal({
  closeModal = () => {},
  filter = null,
  formData = {},
  agentsOptions = [],
  applyFilters = () => {},
  setSelectedFilterData = () => {}
}) {
  // Redux hooks
  const dispatch = useDispatch();
  const { zohoData } = useSelector(state => state.user);

  // Local state
  const [newName, setNewName] = useState(() => (filter ? filter.name : ''));
  const [newDescription, setNewDescription] = useState(() =>
    filter ? filter.description || '' : ''
  );

  const [nameError, setNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');

  const handleNameChange = e => {
    const value = e.target.value;
    setNewName(value);
    if (!value.trim()) {
      setNameError('Filter name is required');
    } else if (value.length >= 50) {
      setNameError('Maximum 50 characters reached');
    } else {
      setNameError('');
    }
  };

  const handleDescriptionChange = e => {
    const value = e.target.value;
    setNewDescription(value);
    if (value.length >= 250) {
      setDescriptionError('Maximum 250 characters reached');
    } else {
      setDescriptionError('');
    }
  };

  useEffect(() => {
    if (!newName.trim()) {
      setNameError('Filter name is required');
    }
  }, []);

  function updateFilter() {
    const allAgentsSelected = formData.agents.length === agentsOptions.length;
    const payload = {
      searchTerm: formData.searchTerm,
      carriers: formData.carriers,
      agents: allAgentsSelected ? 'ALL AGENTS SELECTED' : formData.agents,
      mktp_state: formData.mktp_state,
      typeFilter: formData.typeFilter,
      bobFilter: formData.bobFilter,
      carrierApptStatus: formData.carrierApptStatus
    };
    dispatch(
      handleUpdateFilter(
        filter.id,
        newName,
        newDescription,
        payload,
        message => {
          dispatch(showAlert({ message, code: 'error' }));
        }
      )
    );
    setSelectedFilterData(formData);
    applyFilters();
    closeModal();
  }

  function saveFilter() {
    if (!newName.trim()) {
      setNameError('Filter name is required');
      return;
    }
    const allAgentsSelected = formData.agents.length === agentsOptions.length;
    const payload = {
      searchTerm: formData.searchTerm,
      carriers: formData.carriers,
      agents: allAgentsSelected ? 'ALL AGENTS SELECTED' : formData.agents,
      mktp_state: formData.mktp_state,
      typeFilter: formData.typeFilter,
      bobFilter: formData.bobFilter,
      carrierApptStatus: formData.carrierApptStatus
    };
    dispatch(
      handleSaveFilter(
        zohoData.id,
        newName,
        newDescription,
        payload,
        'submissions',
        false,
        message => dispatch(showAlert({ message, code: 'error' }))
      )
    );
    closeModal();
  }

  return (
    <>
      <Modal
        size='large'
        title='Save Filter'
        active={true}
        closeModal={closeModal}
      >
        <ModalActionConfirmation
          onCancel={closeModal}
          primaryButtonText={filter ? 'Save Changes' : 'Save New Filter'}
          secondaryButtonText={filter ? 'Save as New Filter' : ''}
          onPrimaryConfirm={filter ? updateFilter : saveFilter}
          onSecondaryConfirm={filter ? saveFilter : () => {}}
          primaryButtonDisabled={!!nameError}
          isPrimaryButtonDisabled={newName.trim() === ''}
          isSecondaryButtonDisabled={newName.trim() === ''}
          content={
            <>
              <TextField
                label='Filter Name'
                required
                value={newName}
                maxLength={50}
                inputProps={{ maxLength: 50 }}
                onChange={handleNameChange}
                error={!!nameError}
                helperText={nameError || `${newName.length}/50`}
                FormHelperTextProps={{ style: { fontSize: '0.875rem' } }}
                fullWidth
                margin='normal'
              />
              <TextField
                label='Short Description'
                multiline
                minRows={3}
                inputProps={{ maxLength: 250 }}
                fullWidth
                margin='normal'
                value={newDescription}
                onChange={handleDescriptionChange}
                error={!!descriptionError}
                helperText={descriptionError || `${newDescription.length}/250`}
                FormHelperTextProps={{ style: { fontSize: '0.875rem' } }}
              />
              <h2>Description</h2>
              <p>
                Agents Selected: <b>{formData.agents.length}</b> out of{' '}
                <b>{agentsOptions.length}</b>
              </p>
              <p>
                Selected Carriers:{' '}
                {formData.carriers.length > 0
                  ? formData.carriers.map(c => c.name).join(', ')
                  : 'No carriers selected'}
              </p>
              <p>
                Selected State(es):{' '}
                {formData.mktp_state.length > 0
                  ? formData.mktp_state.join(', ')
                  : 'No states selected'}
              </p>
              <p>
                Carrier Appointment Status:{' '}
                {formData.carrierApptStatus.length > 0
                  ? formData.carrierApptStatus.join(', ')
                  : 'No Carrier Appt status selected'}
              </p>
              <p>
                BOB Status:{' '}
                {formData.bobFilter.length > 0
                  ? formData.bobFilter.join(', ')
                  : 'No BOB status selected'}
              </p>
            </>
          }
        />
      </Modal>
    </>
  );
}

SaveFilterModal.propTypes = {
  closeModal: propTypes.func,
  filter: propTypes.object,
  formData: propTypes.object.isRequired,
  agentsOptions: propTypes.array.isRequired,
  applyFilters: propTypes.func.isRequired,
  setSelectedFilterData: propTypes.func.isRequired
};
